import React from 'react';

import { graphql, Link, PageProps } from 'gatsby';
import { Link as GatsbyLink } from 'gatsby-link';

import { Button, styled, useTheme } from '@mui/material';

import {
  isCleanroom,
  isNonClothing,
  ServiceSegmentCategory,
  ServiceSegmentType,
  ServiceTeaserData,
  ServiceTeaserType,
} from '@mewa/types';
import {
  BreadCrumbData,
  BreadCrumbs,
  CollectionSlider,
  ServiceTeaser,
  useSliderViewport,
} from '@mewa/ui';

import { localeBaseUrl } from '../../libs/alternative-links';
import { marketsConfig } from '../../libs/config/markets';
import t from '../../libs/translate';
import { useConversionButtonLabel } from '../a-b-testing/cta-label-experiment';
import { NavigationData } from '../components/header/types';
import Image from '../components/image';
import { ProductCardKeyFactsWrapper } from '../components/slider/product-card-key-facts-wrapper';
import { ProductsSliderContainer } from '../components/slider/products-slider';
import { industryCardImages, jpgPath } from '../helpers/image-factory';
import { GlobalPageContext } from '../types';
import { IndustryHeroScroller } from './components/industry-hero-scroller';
import { Industry, ProductGroup } from './components/industry-page-types';
import { IndustryStickyTabs } from './components/industry-sticky-tabs';
import { getIndustryTabs } from './industry-page-helper';

export { Head } from '../layouts/head';

type Props = NavigationData & {
  nonClothingServiceSegments: {
    nodes: {
      productGroups: ProductGroup[];
      serviceSegment: {
        name: string;
        category: ServiceSegmentCategory;
        slug: string;
        key: ServiceSegmentType;
        assets: {
          emotion: string | null;
        };
      };
    }[];
  };
  pimIndustry: Industry;
  serviceTestTeaser?: ServiceTeaserData<ServiceTeaserType.cleaningClothsServiceTest>;
  translations: {
    general_more_information: string;
    show_product_details_label: string;
    navigation_product_menu_label: string;
  };
};

type PageContext = GlobalPageContext & {
  id: string;
  categoryType: 'nonClothing' | 'cleanroom';
};

export const query = graphql`
  query (
    $marketRegex: String!
    $lang: String!
    $locale: String!
    $industryId: String!
  ) {
    ...NavigationFragment

    pimIndustry(id: { eq: $industryId }) {
      name
      h1
      h2
      infoText
      slug
      serviceSegments {
        category
        name
        slug
      }
      assets {
        default
        keyVisualClothingDesktop
        keyVisualClothingMobile
        keyVisualNonClothingDesktop
        keyVisualNonClothingMobile
      }
    }

    nonClothingServiceSegments: allPimIndustryServiceSegment(
      filter: {
        industry: { id: { eq: $industryId } }
        serviceSegment: { category: { ne: "MBS" } }
      }
    ) {
      nodes {
        productGroups {
          name
          typeName
          products {
            ...ProductData
          }
        }

        serviceSegment {
          name
          category
          slug
          key
          assets {
            emotion
          }
        }
      }
    }

    serviceTestTeaser: strapiServiceTeaser(
      ServiceType: { eq: "putztuch-service-test" }
      locale: { eq: $locale }
    ) {
      description: Description
      title: Title
      serviceType: ServiceType
    }

    translations: translation(locale: { eq: $locale }) {
      general_more_information
      show_product_details_label
      navigation_product_menu_label
    }
  }
`;

const ProductIndustriesNonClothing = ({
  data: {
    nonClothingServiceSegments: { nodes: serviceSegments },
    pimIndustry: industry,
    serviceTestTeaser,
    translations,
    headerAndFooterTranslations,
    navigationLinks,
  },
  pageContext: { langPrefix, market, locale, featureDev, categoryType },
}: PageProps<Props, PageContext>) => {
  const theme = useTheme();

  const sliderViewport = useSliderViewport();
  const { ctaLabel } = useConversionButtonLabel({
    translations: headerAndFooterTranslations,
  });
  const navLinks = navigationLinks.navigation;

  const tabLinks = getIndustryTabs(industry, langPrefix, navLinks, featureDev);

  const hasCleanroomSegments =
    industry.serviceSegments.filter((s) => isCleanroom(s)).length > 0;

  const selectedIndex =
    categoryType === 'nonClothing' && featureDev && hasCleanroomSegments
      ? tabLinks.length - 2
      : tabLinks.length - 1;

  const isClothingMarket = marketsConfig[market].hasClothingSegments;

  const keyVisuals = isClothingMarket
    ? {
        mobile: industry.assets.keyVisualClothingMobile,
        desktop: industry.assets.keyVisualClothingDesktop,
      }
    : {
        mobile: industry.assets.keyVisualNonClothingMobile,
        desktop: industry.assets.keyVisualNonClothingDesktop,
      };

  const conversion = {
    label: ctaLabel,
    link: `${langPrefix}/${navigationLinks.conversion.contact.slug}`,
  };

  const breadCrumbData: BreadCrumbData[] = [
    {
      name: t(translations, 'navigation_product_menu_label'),
      slug: `${langPrefix}/${navigationLinks.navigation.productMenu.slug}/`,
    },
    {
      name: navigationLinks.navigation.industries.name,
      slug: `${langPrefix}/${navigationLinks.navigation.industries.slug}/`,
    },
    {
      name: industry.name,
      slug: '',
    },
  ];

  const mobileBreadCrumbData: BreadCrumbData = {
    name: navigationLinks.navigation.industries.name,
    slug: `${langPrefix}/${navigationLinks.navigation.industries.slug}/`,
  };

  const categoryServiceSegments = serviceSegments.filter((serviceSegment) =>
    categoryType === 'nonClothing'
      ? isNonClothing(serviceSegment.serviceSegment)
      : isCleanroom(serviceSegment.serviceSegment),
  );

  return (
    <>
      <div
        data-pagefind-meta={`industry_name:${industry.name}`}
        style={{ visibility: 'hidden' }}
      />
      <div
        data-pagefind-meta={`service_segment_name:${navLinks.nonClothing.name}`}
        style={{ visibility: 'hidden' }}
      />
      <div
        data-pagefind-meta={`image:${
          industryCardImages(industry.assets.default)[0].src
        }`}
        style={{ visibility: 'hidden' }}
      />
      <BreadCrumbs
        data={breadCrumbData}
        mobileData={mobileBreadCrumbData}
        localeBaseUrl={localeBaseUrl(locale)}
        LinkComponent={GatsbyLink}
      />
      <IndustryHeroScroller
        title={industry.name}
        subtitle={industry.h1 ?? ''}
        subClaim={industry.h2 ?? ''}
        introText={industry.infoText ?? ''}
        cta={conversion}
        backgroundImage={[
          {
            src: jpgPath(keyVisuals.mobile, 1080, 1728),
            media: `(max-width: ${theme.breakpoints.values.sm - 1}px)`,
            // TODO: kind of large for mobile (the scaled version of the
            // image factory looks pixelated unfortunately). We might need a
            // smaller mobile optimized Version of the image.
            width: 1080,
            height: 1728,
          },
          {
            src: jpgPath(keyVisuals.desktop, 1920, 823),
            media: `(min-width: ${theme.breakpoints.values.sm}px)`,
            width: 1920,
            height: 823,
          },
        ]}
      />
      <IndustryStickyTabs links={tabLinks} selectedIndex={selectedIndex} />

      <ServiceSegmentsWrapper>
        {categoryServiceSegments.map((s, idx) => (
          <React.Fragment key={`${s.serviceSegment.name}-${idx}`}>
            <ProductsSliderContainer>
              <CollectionSlider
                title={s.serviceSegment.name}
                emotion={
                  s.serviceSegment.assets.emotion ? (
                    <Image
                      alt={s.serviceSegment.name}
                      images={[
                        {
                          src: jpgPath(
                            s.serviceSegment.assets.emotion,
                            845,
                            633,
                          ),
                          media: `(min-width: 0px)`,
                          width: 845,
                          height: 633,
                        },
                      ]}
                    />
                  ) : undefined
                }
                // NOTE: non clothing products do not have multiple groups, so we
                // only render the first one
                slides={s.productGroups[0].products}
                renderSlide={(product, idx) => (
                  <ProductCardKeyFactsWrapper
                    key={idx}
                    useMbsFallbackIcon={true}
                    product={product}
                    langPrefix={langPrefix}
                    detailsPageLabel={t(
                      translations,
                      'show_product_details_label',
                    )}
                  />
                )}
                viewport={sliderViewport}
              />
            </ProductsSliderContainer>
            {s.serviceSegment.key === ServiceSegmentType.cleaningCloths &&
              serviceTestTeaser && (
                <ServiceTeaser
                  data-pagefind-ignore
                  description={serviceTestTeaser.description}
                  title={serviceTestTeaser.title}
                  type={ServiceTeaserType.cleaningClothsServiceTest}
                  link={
                    <Button
                      variant="text"
                      color="secondary"
                      component={Link}
                      to={`${langPrefix}/${navigationLinks.conversion.cleaningClothsServiceTestPage.slug}`}
                    >
                      {t(translations, 'general_more_information')}
                    </Button>
                  }
                />
              )}
          </React.Fragment>
        ))}
      </ServiceSegmentsWrapper>
    </>
  );
};

const ServiceSegmentsWrapper = styled('div')(({ theme }) => ({
  marginTop: '52px',

  [theme.breakpoints.up('sm')]: {
    marginTop: '72px',
  },
}));

export default ProductIndustriesNonClothing;
